import { word } from "@modules/core/utils/i18n"
import { getDurationFromSecond } from "@modules/utils/dateUtils"
import { formatNumberWith2Digits, formatNumberWithXDigits } from "@modules/utils/numberUtils"
import dayjs from "dayjs"

export type DurationUnit = "seconds" | "minutes" | "hours"

export function isAHour(durationInS: number) {
  return durationInS >= 3600
}

export function isAMinute(durationInS: number) {
  return durationInS >= 60
}

export function convertDuration4Digits(duration: number, options?: { unitMinutes?: string }) {
  const hasHour = isAHour(duration)
  return convertDurationToString(duration, { ...options, hideSeconds: hasHour, unitHours: "h", noPadStart: hasHour })
}
/***
 * durationInS:seconds
 */
export const convertDurationToString = (
  durationInS: number,
  options?: {
    hideHours?: boolean
    hideMinutes?: boolean
    hideSeconds?: boolean
    unitHours?: string
    unitMinutes?: string
    unitSeconds?: string
    noPadStart?: boolean
  },
): string => {
  if (durationInS >= 3600 * 101) {
    return "∞"
  }

  const hasHour = isAHour(durationInS)
  const hasMinutes = isAMinute(durationInS)

  let format = ""
  if (hasHour && !options?.hideHours) {
    if (options?.noPadStart) {
      format += "H"
    } else {
      format += "HH"
    }
    if (!(options?.hideMinutes && options.hideSeconds) || options.unitHours) {
      format += `[${options?.unitHours ?? ":"}]`
    }
  }

  if (!options?.hideMinutes) {
    if (!hasHour && options?.noPadStart) {
      format += "m"
    } else {
      format += "mm"
    }
    if (!options?.hideSeconds || options.unitMinutes) {
      format += `[${options?.unitMinutes ?? ":"}]`
    }
  }

  if (!options?.hideSeconds) {
    if (!hasMinutes && options?.hideMinutes && options.noPadStart) {
      format += "s"
    } else {
      format += "ss"
    }
    format += options?.unitSeconds ? `[${options.unitSeconds}]` : ""
  }

  const duration = getDurationFromSecond(durationInS)
  const hours = Math.trunc(duration.asHours())

  if (hours >= 24) {
    const dateString = duration.format(format)
    const regexHours = new RegExp(`[0-9]*${options?.unitHours ?? ":"}`)
    return dateString.replace(regexHours, `${hours}${options?.unitHours ?? ":"}`)
  }

  return duration.format(format)
}

export const convertDurationToDate = (duration: number, format: DurationUnit) =>
  dayjs().startOf("day").add(duration, format).toDate()

export const convertDateToDuration = (date: Date, format: DurationUnit) => {
  const now = dayjs().startOf("day")
  const givenTime = dayjs(date)
  const duration = givenTime.diff(now, format)
  return isNaN(duration) ? 0 : duration
}

export function formatDuration(
  days: number,
  hours: number,
  minutes: number,
  seconds?: number,
  options: { showDays?: boolean; showHours?: boolean; showMinutes?: boolean; showSeconds?: boolean } = {
    showDays: false,
    showHours: true,
    showMinutes: true,
    showSeconds: false,
  },
) {
  let result = ""

  if (options.showDays) result += `${formatNumberWithXDigits(days, 3)}${word("global.day.label", { count: 1 })[0]} `
  if (options.showHours) result += `${formatNumberWith2Digits(hours)}${word("global.hour.label", { count: 1 })[0]} `
  if (options.showMinutes)
    result += `${formatNumberWith2Digits(minutes)}${word("global.minute.label", { count: 1 })[0]} `
  if (options.showSeconds && seconds !== undefined)
    result += `${formatNumberWith2Digits(seconds)}${word("global.seconds.label", { count: 1 })[0]} `

  return result.trimEnd()
}

export const convertTimeValueToTimeString = (value: number, format: "00d 00h 00m" | "00h 00m" | "00m 00s") => {
  if (format === "00d 00h 00m")
    return formatDuration(
      Math.floor(value / (24 * 60)),
      Math.floor((value % (24 * 60)) / 60),
      Math.floor(value % 60),
      0,
      {
        showDays: true,
        showHours: true,
        showMinutes: true,
      },
    )
  else if (format === "00h 00m")
    return formatDuration(0, Math.floor((value % (24 * 60)) / 60), Math.floor(value % 60), 0, {
      showDays: false,
      showHours: true,
      showMinutes: true,
    })
  else return `${formatNumberWith2Digits(Math.floor(value / 60))}m ${formatNumberWith2Digits(value % 60)}s`
}

const MINUTES_IN_HOUR = 60
const MINUTES_IN_DAY = 24 * MINUTES_IN_HOUR
const MINUTES_IN_WEEK = 7 * MINUTES_IN_DAY

/**
 * Converts a duration in minutes to a human readable label, eg. "1 heure", "1 heure 30 minutes", "45 minutes"
 */
export const getDurationLabel = (minutes: number): string => {
  if (minutes < MINUTES_IN_HOUR) return `${String(minutes)} ${word("global.minute.label", { count: minutes })}`

  const hours = Math.floor(minutes / MINUTES_IN_HOUR)
  const remainingMinutes = minutes % MINUTES_IN_HOUR
  const hoursLabel = `${String(hours)} ${word("global.hour.label", { count: hours })}`

  if (remainingMinutes === 0) return hoursLabel

  return `${hoursLabel} ${String(remainingMinutes)} ${word("global.minute.label", { count: remainingMinutes })}`
}

/**
 * Converts a duration in minutes to a "prior to the session" label, eg. "2 semaines avant", "1 heure avant"
 */
export const getPriorTimeLabel = (minutes: number): string => {
  if (minutes === 1) return word("global.lastMinutePrior")
  if (minutes % MINUTES_IN_WEEK === 0) return word("global.weekPrior.label", { count: minutes / MINUTES_IN_WEEK })
  if (minutes % MINUTES_IN_DAY === 0) return word("global.dayPrior.label", { count: minutes / MINUTES_IN_DAY })
  if (minutes % MINUTES_IN_HOUR === 0) return word("global.hourPrior.label", { count: minutes / MINUTES_IN_HOUR })
  return word("global.minutePrior.label", { count: minutes })
}

export const convertTimeStringToTimeValue = (format: "00d 00h 00m" | "00h 00m" | "00m 00s", value?: string | null) => {
  if (typeof value === "undefined" || value === null) return null

  if (value === "") return 0

  // Split the input string by space
  const parts = value.split(" ")

  // Extract minutes and seconds parts
  if (format === "00m 00s") {
    const minutes = parseInt(parts[0] ?? "0", 10)
    const seconds = parseInt(parts[1] ?? "0", 10)

    // Calculate total seconds
    return minutes * 60 + seconds
  } else if (format === "00h 00m") {
    const hours = parseInt(parts[0] ?? "0", 10)
    const minutes = parseInt(parts[1] ?? "0", 10)

    // Calculate total minutes
    return hours * 60 + minutes
  } else {
    const days = parseInt(parts[0] ?? "0", 10)
    const hours = parseInt(parts[1] ?? "0", 10)
    const minutes = parseInt(parts[2] ?? "0", 10)

    // Calculate total minutes
    return days * 24 * 60 + hours * 60 + minutes
  }
}

/**
 * Converts a time value to a time object
 * @param value - The time value to convert
 * @param valueUnit - The unit of the given value
 * @param maxLevelOfDetail - The max level of detail to return
 * @returns The time object
 */
export const convertTimeValueToTimeObject = (
  value: number,
  valueUnit: "days" | "hours" | "minutes" | "seconds", // gives the unit of given value
  maxLevelOfDetail: "days" | "hours" | "minutes" | "seconds" = "days", // gives the max level of detail to return
): {
  days?: number
  hours?: number
  minutes?: number
  seconds?: number
} => {
  if (valueUnit === "days") {
    return {
      days: value,
      hours: 0,
      minutes: 0,
      seconds: 0,
    }
  } else if (valueUnit === "hours") {
    if (maxLevelOfDetail === "days") {
      return {
        days: Math.floor(value / 24),
        hours: value % 24,
        minutes: 0,
        seconds: 0,
      }
    } else {
      return {
        hours: value,
        minutes: 0,
        seconds: 0,
      }
    }
  } else if (valueUnit === "minutes") {
    if (maxLevelOfDetail === "days") {
      return {
        days: Math.floor(value / (24 * 60)),
        hours: Math.floor((value % (24 * 60)) / 60),
        minutes: value % 60,
        seconds: 0,
      }
    } else if (maxLevelOfDetail === "hours") {
      return {
        hours: Math.floor(value / 60),
        minutes: value % 60,
        seconds: 0,
      }
    } else {
      return {
        minutes: value,
        seconds: 0,
      }
    }
  } else if (valueUnit === "seconds") {
    if (maxLevelOfDetail === "days") {
      return {
        days: Math.floor(value / (24 * 60 * 60)),
        hours: Math.floor((value % (24 * 60 * 60)) / (60 * 60)),
        minutes: Math.floor((value % (60 * 60)) / 60),
        seconds: value % 60,
      }
    } else if (maxLevelOfDetail === "hours") {
      return {
        hours: Math.floor(value / (60 * 60)),
        minutes: Math.floor((value % (60 * 60)) / 60),
        seconds: value % 60,
      }
    } else if (maxLevelOfDetail === "minutes") {
      return {
        minutes: Math.floor(value / 60),
        seconds: value % 60,
      }
    } else {
      return {
        seconds: value,
      }
    }
  }
  return {
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  }
}

/**
 * Converts a time object to a time value
 * @param timeObject - The time object to convert
 * @param valueUnit - The unit of the given value
 * @returns The time value
 */
export const convertTimeObjectToTimeValue = (
  timeObject: { days?: number; hours?: number; minutes?: number; seconds?: number },
  valueUnit: "days" | "hours" | "minutes" | "seconds",
) => {
  if (valueUnit === "days") {
    return timeObject.days || 0
  } else if (valueUnit === "hours") {
    return (timeObject.days || 0) * 24 + (timeObject.hours || 0)
  } else if (valueUnit === "minutes") {
    return (timeObject.days || 0) * 24 * 60 + (timeObject.hours || 0) * 60 + (timeObject.minutes || 0)
  }
  return (
    (timeObject.days || 0) * 24 * 60 * 60 +
    (timeObject.hours || 0) * 60 * 60 +
    (timeObject.minutes || 0) * 60 +
    (timeObject.seconds || 0)
  )
}
